import React, { useCallback, useContext, useRef, useState, useEffect } from 'react';
import type { FC } from 'react';

import { NewCommentsContext } from '@confluence/comment-context/entry-points/NewCommentsContext.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { ErrorBoundary } from '@confluence/error-boundary/entry-points/ErrorBoundary.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useSubscriptionsDispatchContext } from '@confluence/graphql/entry-points/SubscriptionsContextProvider.ts';
import { useCommentsDataActions } from '@confluence/comments-data/entry-points/useCommentsData.ts';
import { useCommentsPanelActions } from '@confluence/comments-panel-utils/entry-points/useCommentsPanel.ts';
import { useActiveCommentsQuery } from '@confluence/comments-data/entry-points/useActiveCommentsQuery.ts';
import { useIsReactionsEnabled } from '@confluence/comments-hooks/entry-points/useIsReactionsEnabled.ts';
import { useUnreadCommentsActions } from '@confluence/unread-comments/entry-points/useUnreadComments.ts';
import { useViewedComments } from '@confluence/unread-comments/entry-points/useViewedComments.ts';
import { expVal, expValEquals } from '@confluence/feature-experiments';
import { useAllCommentsData } from '@confluence/comments-data-manager/entry-points/useAllCommentsData.ts';
import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode.ts';
import { PageMode } from '@confluence/page-utils/entry-points/enums.ts';

import {
	MissedPubSubCommentEventsHandler,
	CommentEventType,
	type MissedCommentsResult,
} from '../MissedPubSubCommentEventsHandler.ts';

import { setUpPageCommentsPubSub, removePageCommentsPubSub } from './pageCommentsPubSub.ts';
import { reloadAndAddPageComments } from './pageCommentsOperations.ts';

type PageCommentsPubSubHandlerProps = {
	contentId: string;
	contentType?: string | null;
};

export const PageCommentsPubSubHandler: FC<PageCommentsPubSubHandlerProps> = ({
	contentId,
	contentType,
}) => {
	const isReactionsEnabled = useIsReactionsEnabled(contentId);
	const { addNewComment, removeComment, clearNewComments } = useContext(NewCommentsContext);
	const { addSubscriber, removeSubscriber, setContentType, setContentId } =
		useSubscriptionsDispatchContext() || {};
	const subscriptions = useRef<{ delta: string; subscriberId: string }[]>([]);
	const { userId } = useSessionData();

	const { updateUnreadCommentIDSet } = useUnreadCommentsActions();
	const { getViewedCommentsCacheOnly } = useViewedComments({ pageId: contentId });
	const pageMode = useGetPageMode();

	const {
		addReplyToCommentThread,
		addNewCommentThreads,
		getCurrentCommentsPanelState,
		updateCommentBody,
		updateRemovedCommentIdsMap,
		reopenParentComment,
	} = useCommentsDataActions();

	const { setReplyToCommentId, getCommentsPanelState } = useCommentsPanelActions();

	const clearReplyIfDeleted = useCallback(
		({ commentId }: { commentId: string }) => {
			const { replyToCommentId } = getCommentsPanelState();

			if (replyToCommentId === commentId) {
				setReplyToCommentId();
			}
		},
		[getCommentsPanelState, setReplyToCommentId],
	);

	// Control skip state - start with true to prevent query on mount, set to false when events are detected
	const [shouldSkipQuery, setShouldSkipQuery] = useState(true);

	// Reset skip state when contentId changes (new page)
	useEffect(() => {
		setShouldSkipQuery(true);
	}, [contentId]);

	const { deleteCommentInCache, addCommentInCache } = useAllCommentsData({
		contentId,
		options: {
			skip: true, // Only using write operations, no need to fetch data
		},
	});

	// Implementation using ActiveCommentsQuery
	// Skip is controlled by state - starts as true, set to false when handleEventFetchSuccess is called
	const { refetch: activeCommentsRefetch } = useActiveCommentsQuery({
		pageId: contentId,
		type: 'FOOTER',
		skip: shouldSkipQuery || !contentId,
		fetchPolicy: 'cache-and-network',
	});

	const subscribeToPageCommentsPubSub = useCallback(() => {
		// Early return if pageMode is Edit - do not setup pubsub for edit pages
		if (
			pageMode === PageMode.EDIT &&
			expValEquals('confluence_comment_footercomments_editpage_exp', 'isEnabled', true)
		) {
			return;
		}

		if (
			subscriptions.current.length > 0 &&
			expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)
		) {
			return;
		}

		const queryArgs = {
			isReactionsEnabled,
		};

		subscriptions.current =
			setUpPageCommentsPubSub({
				contentId,
				contentType,
				userId,
				reloader: activeCommentsRefetch,
				addNewComment,
				removeComment,
				queryArgs,
				addNewCommentThreads: expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)
					? () => {}
					: addNewCommentThreads,
				addReplyToCommentThread: expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)
					? () => {}
					: addReplyToCommentThread,
				updateCommentBody: expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)
					? () => {}
					: updateCommentBody,
				updateRemovedCommentIdsMap,
				reopenParentComment,
				getCurrentCommentsPanelState,
				addSubscriber,
				setContentType,
				setContentId,
				updateUnreadCommentIDSet,
				getViewedCommentsCacheOnly,
				addCommentInCache,
				deleteCommentInCache,
				clearReplyIfDeleted,
			}) ?? [];
	}, [
		contentId,
		contentType,
		userId,
		activeCommentsRefetch,
		addNewComment,
		removeComment,
		isReactionsEnabled,
		addNewCommentThreads,
		addReplyToCommentThread,
		updateCommentBody,
		updateRemovedCommentIdsMap,
		reopenParentComment,
		getCurrentCommentsPanelState,
		addSubscriber,
		setContentType,
		setContentId,
		updateUnreadCommentIDSet,
		getViewedCommentsCacheOnly,
		addCommentInCache,
		deleteCommentInCache,
		pageMode,
		clearReplyIfDeleted,
	]);

	const unsubscribeFromPageCommentsPubSub = useCallback(() => {
		// Early return if pageMode is Edit - no pubsub was set up for edit pages
		if (
			pageMode === PageMode.EDIT &&
			expValEquals('confluence_comment_footercomments_editpage_exp', 'isEnabled', true)
		) {
			return;
		}

		if (
			subscriptions.current.length === 0 &&
			expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)
		) {
			return;
		}

		const queryArgs = {
			isReactionsEnabled,
		};

		removePageCommentsPubSub({
			contentId,
			contentType,
			userId,
			reloader: activeCommentsRefetch,
			addNewComment,
			removeComment,
			queryArgs,
			updateRemovedCommentIdsMap,
			removeSubscriber,
			subscriptions: subscriptions.current,
			getViewedCommentsCacheOnly,
		});

		if (expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)) {
			subscriptions.current = [];
		}
	}, [
		contentId,
		contentType,
		userId,
		activeCommentsRefetch,
		addNewComment,
		removeComment,
		isReactionsEnabled,
		updateRemovedCommentIdsMap,
		removeSubscriber,
		getViewedCommentsCacheOnly,
		pageMode,
	]);

	const handleEventFetchSuccess = useCallback(
		(data: MissedCommentsResult) => {
			if (data && data.contentId === contentId) {
				// Enable the query so refetch can work
				setShouldSkipQuery(false);
				if (data.commentEvents.length) {
					const commentData = data.commentEvents.map((event) => {
						return { commentId: event.id, accountId: event.accountId };
					});
					void reloadAndAddPageComments(activeCommentsRefetch, addNewComment, commentData, userId);
				}
			}
		},
		[contentId, addNewComment, activeCommentsRefetch, userId],
	);

	if (
		!userId ||
		(pageMode === PageMode.EDIT &&
			expValEquals('confluence_comments_get_document_version_number', 'isEnabled', true))
	) {
		return null;
	} else {
		return (
			<ErrorBoundary attribution={Attribution.COLLABORATION}>
				<MissedPubSubCommentEventsHandler
					contentId={contentId}
					eventType={CommentEventType.FOOTER}
					subscribeToPubSubEvents={subscribeToPageCommentsPubSub}
					unsubscribeFromPubSubEvents={unsubscribeFromPageCommentsPubSub}
					onEventFetchSuccess={handleEventFetchSuccess}
					onDocumentReturn={clearNewComments}
					isAutoReloadPageComments
				/>
			</ErrorBoundary>
		);
	}
};
